import { useContext, useEffect, useRef, useState } from "react";
import { alpha, Box, Button, CircularProgress, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { queryClient } from "../../App";
import axios from "../../api/axios";
import AuthContext from "../../context/AuthProvider";
import logo from "../../utils/images/ridePlus.png";
import useOtpResendCooldown from "../../hooks/useOtpResendCooldown";
import useTenantBranding from "../../hooks/useTenantBranding";
import AuthLayout from "../../components/auth/AuthLayout";
import AuthTextField from "../../components/auth/AuthTextField";
import PrimaryButton from "../../components/auth/PrimaryButton";
import SecondaryButton from "../../components/auth/SecondaryButton";
import Avatar from "@mui/material/Avatar";

// Forget Password Component
function ForgetPassword({ setOpen }) {
  const { tenantLogo, hideDefaultBranding } = useTenantBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const { redirectUri } = useContext(AuthContext);
  const searchParams = new URLSearchParams(location.search);
  const redirectSearchParams = new URLSearchParams(redirectUri?.search || "");
  const clientIdForReset =
    searchParams.get("client_id") ||
    searchParams.get("clientId") ||
    redirectSearchParams.get("client_id") ||
    redirectSearchParams.get("clientId") ||
    location.state?.client_id ||
    location.state?.clientId;

  // USESTATE & USEREF
  const [openDrawer, setOpenDrawer] = useState(false);
  const [requestOtp, setRequestOtp] = useState(false);

  const password = useRef({});
  const email = useRef({});
  const cooldownHook = useOtpResendCooldown("forgetPassword", email.current);

  // USEFORM HOOK
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({ mode: "onChange" });

  email.current = watch("email", "");
  password.current = watch("password", "");

  // SNACKBAR
  const { enqueueSnackbar } = useSnackbar();

  const { isFetching: otpIsFetching, refetch, isFetched } = useQuery(
    ["otp-forget-password", email.current, clientIdForReset],
    () =>
      axios.get("/resetCode", {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          email: email.current,
          ...(clientIdForReset && { client_id: clientIdForReset }),
        },
      }),
    {
      refetchOnWindowFocus: false,
      enabled: requestOtp,
      retry: false,
    }
  );
  const { mutate, isLoading, data, error } = useMutation((pwd) => {
    return axios.post(`/resetPassword`, pwd);
  });

  // HELPERS — close back to caller if embedded, else navigate to login.
  const close = () =>
    setOpen
      ? setOpen(false)
      : navigate(`/login${location.search || ""}`, {
          replace: true,
          state: location.state,
        });
  const onSubmit = () => {
    refetch();
  };
  const onReset = (newData) => {
    const toBeSent = { ...newData, email: email.current };
    mutate(toBeSent);
  };

  // USEFFECT
  useEffect(() => {
    if (data) {
      enqueueSnackbar(`Succesfully changed your password.`, {
        variant: "success",
        preventDuplicate: true,
        autoHideDuration: 2000,
      });
      queryClient.clear();
      close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, enqueueSnackbar]);
  useEffect(() => {
    if (isFetched) {
      enqueueSnackbar(`Code sent.`, {
        variant: "success",
        preventDuplicate: true,
        autoHideDuration: 2000,
      });
      cooldownHook.startCooldown(); // Start cooldown after successful OTP request
      setOpenDrawer(true);
      queryClient.clear();
      setRequestOtp(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched, enqueueSnackbar]);
  useEffect(() => {
    if (error) {
      enqueueSnackbar(
        error?.response?.data?.error?.message ||
        error?.message ||
        "Network Error!",
        {
          variant: "error",
          preventDuplicate: true,
          autoHideDuration: 2000,
        }
      );
      queryClient.clear();
      setRequestOtp(false);
    }
  }, [enqueueSnackbar, error]);

  const brandingAvatar = (tenantLogo || !hideDefaultBranding) && (
    <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
      <Avatar
        sx={{ width: "140px", height: "100px", borderRadius: 1 }}
        src={tenantLogo || logo}
      />
    </Box>
  );

  return (
    <>
      {/* Step 1 — enter email */}
      <AuthLayout
        title="Recover Password"
        subtitle="Enter your email to receive a reset code"
        header={brandingAvatar}
        onSubmit={handleSubmit(onSubmit)}
        footer={
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: 1.5,
            }}
          >
            <SecondaryButton onClick={close}>Back</SecondaryButton>
            <PrimaryButton type="submit" loading={otpIsFetching}>
              Next
            </PrimaryButton>
          </Box>
        }
      >
        <Controller
          name="email"
          control={control}
          rules={{
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          }}
          render={({ field }) => (
            <AuthTextField
              variant="email"
              label="Email Address"
              autoFocus
              error={errors?.email}
              helperText={errors?.email ? errors?.email?.message : ""}
              {...field}
            />
          )}
        />
      </AuthLayout>

      {/* Step 2 — enter reset code + new password */}
      {openDrawer && email.current && (
        <AuthLayout
          title="Recover Password"
          header={brandingAvatar}
          onSubmit={handleSubmit(onReset)}
          footer={
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "row" },
                gap: 1.5,
              }}
            >
              <SecondaryButton onClick={() => setOpenDrawer(false)}>
                Back
              </SecondaryButton>
              <PrimaryButton
                type="submit"
                loading={isLoading}
                sx={{
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  boxShadow: (t) =>
                    `0 3px 5px 2px ${alpha(t.palette.primary.main, 0.3)}`,
                  "&:hover": {
                    bgcolor: "primary.dark",
                    boxShadow: (t) =>
                      `0 4px 8px 2px ${alpha(t.palette.primary.main, 0.4)}`,
                  },
                }}
              >
                Submit
              </PrimaryButton>
            </Box>
          }
        >
          <Typography
            sx={{
              fontSize: { xs: 14, sm: 15 },
              mb: 2,
              color: "text.secondary",
              lineHeight: 1.6,
            }}
          >
            Please put the short code sent via SMS to the phone linked with the
            email:{" "}
            <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>
              {email.current}
            </Box>{" "}
            and then set your new password.
          </Typography>
          <Controller
            name="reset_code"
            control={control}
            rules={{ required: "Code can't be empty" }}
            render={({ field }) => (
              <AuthTextField
                variant="password"
                label="Code"
                autoComplete="one-time-code"
                disabled={cooldownHook.isBlocked}
                error={errors?.reset_code}
                helperText={errors?.reset_code ? errors.reset_code.message : null}
                {...field}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={{
              required: "Password is required",
              minLength: { value: 6, message: "At least 6 characters is required." },
              maxLength: { value: 32, message: "The maximum characters allowed is 32." },
            }}
            render={({ field }) => (
              <AuthTextField
                variant="password"
                label="New Password"
                autoComplete="new-password"
                error={errors?.password}
                helperText={errors?.password ? errors.password.message : null}
                {...field}
              />
            )}
          />
          <Controller
            name="confirm_password"
            control={control}
            rules={{
              validate: (value) =>
                value === password.current || "The passwords do not match",
            }}
            render={({ field }) => (
              <AuthTextField
                variant="password"
                label="Confirm Password"
                autoComplete="new-password"
                error={errors?.confirm_password}
                helperText={
                  errors?.confirm_password
                    ? errors.confirm_password.message
                    : null
                }
                {...field}
              />
            )}
          />
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <Button
              size="small"
              variant="text"
              sx={{
                textTransform: "none",
                color: cooldownHook.isOnCooldown ? "text.disabled" : "primary.main",
                "&:hover": { bgcolor: "transparent" },
              }}
              onClick={() => {
                if (cooldownHook.canResend) {
                  setRequestOtp(true);
                }
              }}
              disabled={otpIsFetching || !cooldownHook.canResend}
            >
              {cooldownHook.isOnCooldown
                ? `Resend OTP (${cooldownHook.formattedTime})`
                : "Resend OTP"}
              {otpIsFetching && (
                <CircularProgress
                  size={16}
                  sx={{ ml: 1, color: "primary.main" }}
                />
              )}
            </Button>
          </Box>
        </AuthLayout>
      )}
    </>
  );
}
export default ForgetPassword;
